import { Title, Text, Group, Stack, PasswordInput, Stepper } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserData } from 'hooks/api/useUser';
import { PortalButton } from 'components/Button/PortalButton';
import { validateRequired } from 'utils/validation';
import { useDrawerContext } from 'context/hooks';
import { useDisableMfa } from 'hooks/api/user/mfa/useDisableMfa';
import { ConfirmPresenceResult, useConfirmPresence } from 'hooks/api/user/mfa/useConfirmPresence';

export const TwoStepAuthDisable = () => {
  const { t } = useTranslation();
  const user = useUserData();
  const [activeStep, setActiveStep] = useState(0);
  const [confirmPresenceToken, setConfirmPresenceToken] = useState('');
  const { closeDrawer } = useDrawerContext();
  const { mutate: confirmPresenceQuery, isPending: isConfirmingPresence } = useConfirmPresence();
  const { mutate: disableMfaQuery, isPending: isDisabling } = useDisableMfa();

  const passwordForm = useForm({
    initialValues: {
      password: '',
    },
    validate: {
      password: (value) => validateRequired({ value, label: t('password_label') }),
    },
  });

  const handleNextStep = () => {
    passwordForm.validate();
    if (activeStep === 0 && passwordForm.isValid()) {
      confirmPresenceQuery({
        password: passwordForm.values.password,
        callback: (result: ConfirmPresenceResult) => {
          setConfirmPresenceToken(result.confirmPresenceToken.token);
          setActiveStep(activeStep + 1);
        },
      });
    }

    if (activeStep === 1) {
      disableMfaQuery({ userId: user.data?.user.id, presenceToken: confirmPresenceToken });
    }
  };

  const isLoading = isConfirmingPresence || isDisabling;

  const buttonGroup = (confirmText: string) => {
    return (
      <Group mt={'lg'} justify={'flex-end'}>
        <PortalButton
          variant={'outline'}
          label={t('cancel_label')}
          onClick={() => closeDrawer()}
          disabled={isLoading}
        />
        <PortalButton
          type={'submit'}
          color={'var(--mantine-color-red-5)'}
          loading={isLoading}
          label={confirmText}
          disabled={isLoading}
        />
      </Group>
    );
  };

  return (
    <Stack gap={'md'}>
      <Title order={3}>{t('mfa_disable_title')}</Title>
      <Stepper color={'var(--mantine-color-red-5)'} size={'sm'} active={activeStep}>
        <Stepper.Step label={t('mfa_disable_step_one')} description={t('mfa_disable_step_one_description')}>
          <Stack>
            <Text>{t('mfa_description')}</Text>
            <form onSubmit={passwordForm.onSubmit(handleNextStep)}>
              <PasswordInput placeholder={t('password_label')} {...passwordForm.getInputProps('password')} />
              {buttonGroup(t('continue_label'))}
            </form>
          </Stack>
        </Stepper.Step>
        <Stepper.Step label={t('mfa_disable_step_two')} description={t('mfa_disable_step_two_description')}>
          <Stack>
            <form onSubmit={passwordForm.onSubmit(handleNextStep)}>
              <Text>{t('mfa_disable_confirmation_text')}</Text>
              <Text>{t('mfa_disable_confirmation_text_two')}</Text>
              {buttonGroup(t('mfa_disable_title'))}
            </form>
          </Stack>
        </Stepper.Step>
      </Stepper>
    </Stack>
  );
};
